import React, { useState } from 'react';
import { styles } from './styles';
import SectionWrapper from '../../components/sectionWrapper/sectionWrapper';
import ReflectionButton from '../../components/reflectionButton/reflectionButton';
import { RIPPLE_ON_LIGHT } from '../../globalStyles';
import { sendContactMessage, ContactError } from '../../contact/client';
import { CONTACT_EMAIL } from '../../contact/config';

type Status = 'idle' | 'sending' | 'sent' | 'error';
type Field = 'name' | 'email' | 'subject' | 'message';

// `contactExtra` is the honeypot. Named neutrally (NOT "company"/"name"/etc.) so
// browser autofill won't populate it and silently flag a real visitor as a bot.
const EMPTY = { name: '', email: '', subject: '', message: '', contactExtra: '' };
const ORDER: Field[] = ['name', 'email', 'subject', 'message'];
// Same loose check the server uses — enough to catch obvious typos, not RFC-strict.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Client-side validation mirrors the server's required-field + email checks, so a
// bad submit is caught instantly instead of after a round-trip (which would also
// waste a rate-limit slot). The server still re-validates — this is just UX.
function validate(f: typeof EMPTY): Partial<Record<Field, string>> {
  const errs: Partial<Record<Field, string>> = {};
  if (!f.name.trim()) errs.name = 'Please enter your name.';
  if (!f.email.trim()) errs.email = 'Please enter your email.';
  else if (!EMAIL_RE.test(f.email.trim())) errs.email = 'Please enter a valid email address.';
  if (!f.subject.trim()) errs.subject = 'Please add a subject.';
  if (!f.message.trim()) errs.message = 'Please enter a message.';
  return errs;
}

const Contact: React.FC = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({});

  const set =
    (field: keyof typeof EMPTY) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((f) => ({ ...f, [field]: e.target.value }));
      // Clear this field's error and any prior send error as the user corrects it.
      if (fieldErrors[field as Field]) {
        setFieldErrors((fe) => ({ ...fe, [field]: undefined }));
      }
      if (status === 'error') {
        setStatus('idle');
        setError('');
      }
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending' || status === 'sent') return;

    // 1. Client-side validation — block the send and surface field errors.
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setStatus('idle');
      setError('');
      const firstInvalid = ORDER.find((k) => errs[k]);
      if (firstInvalid) document.getElementById(`contact-${firstInvalid}`)?.focus();
      return;
    }

    // 2. Send.
    setFieldErrors({});
    setStatus('sending');
    setError('');
    try {
      await sendContactMessage(form);
      setStatus('sent');
      setForm(EMPTY);
    } catch (err) {
      const ce = err as ContactError;
      setError(
        ce.code === 'unconfigured'
          ? `Please email us directly at ${CONTACT_EMAIL}.`
          : ce.message || 'Something went wrong. Please try again.',
      );
      setStatus('error');
    }
  };

  const sending = status === 'sending';
  const sent = status === 'sent';

  const fieldStyle = (field: Field) => ({
    ...styles.input,
    ...(fieldErrors[field] ? styles.fieldInvalid : {}),
  });

  return (
    <SectionWrapper id="contact" label="Contact" heading="Let's build something">
      <div className="contact-inner" style={styles.inner}>
        {/* Info column */}
        <div>
          <p style={styles.infoPara}>
            Have a project in mind, or just want to learn more about what we do? Reach out. We
            respond within one business day. Every project is personally overseen by Kaan Sozer, our
            founder.
          </p>
          <div style={styles.details}>
            <div style={styles.detailItem}>
              <span style={styles.dot} />
              <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.dot} />
              <span>Arizona, USA</span>
            </div>
            <div style={styles.detailItem}>
              <span style={styles.dot} />
              <a href="https://novaeon.io" target="_blank" rel="noreferrer" style={styles.link}>
                novaeon.io
              </a>
            </div>
          </div>
        </div>

        {/* Form column */}
        <form style={styles.form} onSubmit={handleSubmit} noValidate>
          <div className="contact-formrow" style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="contact-name">Name</label>
              <input
                id="contact-name"
                name="name"
                style={fieldStyle('name')}
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={set('name')}
                aria-invalid={!!fieldErrors.name}
                disabled={sending || sent}
              />
              {fieldErrors.name && <span style={styles.fieldErrorText}>{fieldErrors.name}</span>}
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="contact-email">Email</label>
              <input
                id="contact-email"
                name="email"
                style={fieldStyle('email')}
                type="email"
                placeholder="you@company.com"
                value={form.email}
                onChange={set('email')}
                aria-invalid={!!fieldErrors.email}
                disabled={sending || sent}
              />
              {fieldErrors.email && <span style={styles.fieldErrorText}>{fieldErrors.email}</span>}
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="contact-subject">Subject</label>
            <input
              id="contact-subject"
              name="subject"
              style={fieldStyle('subject')}
              type="text"
              placeholder="What can we help with?"
              value={form.subject}
              onChange={set('subject')}
              aria-invalid={!!fieldErrors.subject}
              disabled={sending || sent}
            />
            {fieldErrors.subject && <span style={styles.fieldErrorText}>{fieldErrors.subject}</span>}
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="contact-message">Message</label>
            <textarea
              id="contact-message"
              name="message"
              style={{ ...styles.textarea, ...(fieldErrors.message ? styles.fieldInvalid : {}) }}
              rows={5}
              placeholder="Tell us about your project or question..."
              value={form.message}
              onChange={set('message')}
              aria-invalid={!!fieldErrors.message}
              disabled={sending || sent}
            />
            {fieldErrors.message && <span style={styles.fieldErrorText}>{fieldErrors.message}</span>}
          </div>

          {/* Honeypot — hidden from real users; bots that fill it are dropped.
              Neutral name + no keyword label + autocomplete off so browsers don't
              autofill it for real visitors. */}
          <div style={styles.honeypot} aria-hidden="true">
            <label htmlFor="contact-extra">Leave this field empty</label>
            <input
              id="contact-extra"
              name="contactExtra"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.contactExtra}
              onChange={set('contactExtra')}
            />
          </div>

          <ReflectionButton
            type="submit"
            style={{ ...styles.submit, opacity: sending || sent ? 0.6 : 1 }}
            highlightColor={RIPPLE_ON_LIGHT}
            disabled={sending || sent}
          >
            {sending ? 'Sending…' : sent ? 'Message sent' : 'Send message'}
          </ReflectionButton>

          {status === 'error' && (
            <p style={{ ...styles.feedback, ...styles.feedbackError }} role="alert">
              {error}
            </p>
          )}
          {sent && (
            <p style={{ ...styles.feedback, ...styles.feedbackSuccess }} role="status">
              Thanks, we'll be in touch within one business day.
            </p>
          )}
        </form>
      </div>
    </SectionWrapper>
  );
};

export default Contact;
